<script setup lang="ts">
import { reactive, ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import Modal from './Modal.vue';
import { Notifications, notify } from '@kyvg/vue3-notification';
import { DatePicker } from 'v-calendar';
import { useValidationHelpers } from '@/helpers/validation.helper';
import { Tooltip } from 'floating-vue';
import leadCaptureRepository, { LeadCaptureRequestModel } from '@/repositories/lead-capture.repository';

const isSubmitting = ref(false)
const now = new Date();

const props = defineProps<{
    show: boolean;
    externalUrl?: string;
    clubName?: string
}>();

const emits = defineEmits<{
    (e: "close"): void;
}>();

// ---- Form State ----
interface LeadForm {
    firstname: string
    email: string
    gender: string
    birthdate?: Date
    newsletter: boolean
}

const form = reactive<LeadForm>({
    firstname: '',
    email: '',
    gender: '',
    birthdate: undefined,
    newsletter: false
})

// ---- Validation Rules ----
const rules = {
    firstname: {required},
    email: { required, email }
}

const v$ = useVuelidate(rules, form)
const { getFieldErrors } = useValidationHelpers(v$);

// ---- Methods ----

const handleSubmit = async () => {
    isSubmitting.value = true
    const isValid = await v$.value.$validate()

    if (!isValid) {
        isSubmitting.value = false
        return
    }

    try {
        const request: LeadCaptureRequestModel = {
            email: form.email,
            firstname: form.firstname,
            newsletter: form.newsletter,
            birthdate: form.birthdate ?? undefined,
            gender: form.gender != '' ? form.gender : undefined,
            clubName: props.clubName != '' ? props.clubName : undefined,
        }
        
        await leadCaptureRepository.subscribe(request);

        redirect()
    } catch (error) {
        notify({
            type: 'error',
            title: 'Er is een fout opgetreden bij opslaan van de gegevens',
            duration: 20000
        })
    } finally {
        isSubmitting.value = false
    }
}

const skipAndContinue = () => {
    redirect()
}

const redirect = () => {
    window.open(props.externalUrl, '_self')
    emits('close')
}

const resetForm = () => {
    form.firstname = '';
    form.email = '';
    form.gender = '';
    form.birthdate = undefined;
    form.newsletter = false;

    v$.value.$reset();
}

const cancel = () => {
    resetForm();
    emits("close");
};
</script>

<template>
    <Notifications position="top right" classes="custom-notifications" animation-type="velocity" aria-live="polite"
        aria-atomic="true" />

    <Modal :show="props.show" @close="cancel" size="md" custom-class="lead-capture-modal">
        <button class="close-modal-btn" @click="cancel" aria-label="Sluit modal">
            <span class="icon-close"></span>
        </button>
        <div class="lead-capture-modal-content">

            <h2 class="h4 mb-0">Alles wat je nodig hebt voor een vliegende start</h2>

            <div class="lead-capture-modal-content__image-with-text">
                <img class="lead-capture-modal-content__image-with-text-image"
                    :src="`/Images/golfstart/beginnen-met-golf-boekje.jpg`" />
                <p class="lead-capture-modal-content__image-with-text-text mb-0">
                    Voordat je doorklikt: Ontvang gratis onze gids “Beginnen met Golf” met praktische tips voor je
                    eerste keer op de golfbaan.
                </p>
            </div>


            <form class="lead-capture-modal-content__form">
                <div>
                    <label for="lead-firstname">Voornaam *</label>
                    <input id="lead-firstname" class="form-control" type="text" v-model="form.firstname"
                        placeholder="voornaam" :class="{ 'is-invalid': v$.firstname.$error }" />
                    <div v-for="err in getFieldErrors('firstname')" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>

                <div>
                    <label for="lead-email">E-mailadres *</label>
                    <input id="lead-email" class="form-control" type="email" v-model="form.email"
                        placeholder="naam@domein.nl" :class="{ 'is-invalid': v$.email.$error }" />
                    <div v-for="err in getFieldErrors('email')" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>

                    <p class="form-control-information">We gebruiken je e-mail om de gids te sturen en je maximaal 2-3 keer te mailen met vervolgmails. Je kunt je altijd afmelden.</p>
                </div>

                <div class="lead-capture-modal-content__form-columns">
                    <div>
                        <label for="lead-gender">
                            <Tooltip :triggers="['click', 'hover']">
                                Geslacht <span class="icon icon-info1" aria-hidden="true"></span>
                                <template #popper>
                                    <div style="background: none; font-size: 0.875rem; line-height: 1rem;">
                                        Helpt ons om tips te personaliseren. Wordt anoniem verwerkt en is optioneel.
                                    </div>
                                </template>
                            </Tooltip>
                        </label>
                        <select id="lead-gender" class="custom-select" v-model="form.gender">
                            <option value="" disabled selected>Kies een geslacht</option>
                            <option value="m">Man</option>
                            <option value="v">Vrouw</option>
                            <option value="x">Onbekend</option>
                        </select>
                    </div>

                    <div>
                        <label for="lead-birthdate">
                            <Tooltip :triggers="['click', 'hover']">
                                Geboortedatum <span class="icon icon-info1" aria-hidden="true"></span>
                                <template #popper>
                                    <div style="background: none; font-size: 0.875rem; line-height: 1rem;">
                                        Helpt ons om tips te personaliseren. Wordt anoniem verwerkt en is optioneel.
                                    </div>
                                </template>
                            </Tooltip>
                        </label>
                        <DatePicker v-model="form.birthdate" :popover="{ visibility: 'click' }" locale="nl"
                            color="platform-theme" :max-date="now">
                            <template v-slot="{ inputValue, inputEvents }">
                                <input id="lead-birthdate" class="form-control" :value="inputValue" v-on="inputEvents"
                                    placeholder="DD-MM-JJJJ" />
                            </template>

                            <template #header-prev-button>
                                <span class="sr-only">Vorige maand</span>
                                <span class="icon-chevron--left" aria-hidden="true"></span>
                            </template>
                            <template #header-next-button>
                                <span class="sr-only">Volgende maand</span>
                                <span class="icon-chevron--right" aria-hidden="true"></span>
                            </template>
                        </DatePicker>
                    </div>
                </div>

                <!-- Newsletter -->
                <div class="custom-control custom-checkbox">
                    <input id="lead-newsletter" class="custom-control-input" type="checkbox"
                        v-model="form.newsletter" />
                    <label for="lead-newsletter" class="custom-control-label mb-0">Ja, houd mij op de hoogte van golf, tips
                        en aanbiedingen
                        per e-mail.</label>
                </div>
            </form>

            <button type="button" class="btn btn-secondary lead-capture-modal-content__submit-btn" @click="handleSubmit"
                :disabled="isSubmitting">
                Ontvang onze gids en ga verder
                <span class="icon icon-arrow-right" aria-hidden="true"></span>
            </button>

            <a href="#" class="lead-capture-modal-content__skip-link" @click.prevent="skipAndContinue">
                Ga verder zonder gids
            </a>
        </div>
    </Modal>
</template>